import React from "react";
import ReactDOM from "react-dom";
import "./index.scss";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import reducer from "../src/Redux/Reducer";
import "./i18n";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import GlobalTheme from "./GlobalTheme.js";
import { MemoryRouter } from "react-router";
import history from "./Pages/history/history";
import { createStore, applyMiddleware, compose } from "redux";
import { persistStore, persistReducer } from "redux-persist";
import thunk from "redux-thunk";
import storage from "redux-persist/lib/storage/session";
import { PersistGate } from "redux-persist/integration/react";
import { firebaseConfig as firebaseConfigEnv } from "./firebase-config";
import { initializeApp } from "firebase/app";

// HIPAA safeguard: silence non-essential console output in production so PHI
// logged during development never surfaces in an end user's browser console.
// warn/error are preserved for genuine diagnostics; avoid logging PHI through them.
if (process.env.NODE_ENV === "production") {
  const noop = () => {};
  console.log = noop;
  console.debug = noop;
  console.info = noop;
}

// When a new version is deployed while a tab is open, lazy-loaded chunks the
// running app references are replaced with new content-hashed filenames, so
// the old ones 404 and dynamic import throws a ChunkLoadError. Reload once to
// pull the fresh index.html (served no-cache) and its current chunk hashes.
// A sessionStorage guard prevents a reload loop if the failure is not stale.
const CHUNK_RELOAD_KEY = "omnicure_chunk_reloaded_at";
const isChunkLoadError = (value) => {
  const message =
    (typeof value === "string" && value) ||
    value?.message ||
    value?.reason?.message ||
    "";
  return (
    /ChunkLoadError/.test(message) ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk [\w-]+ failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
};
const reloadOnceForStaleChunks = (source) => {
  if (!isChunkLoadError(source)) return;
  const last = Number(window.sessionStorage?.getItem(CHUNK_RELOAD_KEY) || 0);
  // Only auto-reload once per 30s window so a genuinely broken deploy or an
  // offline user doesn't get stuck in a refresh loop.
  if (Date.now() - last < 30000) return;
  try {
    window.sessionStorage?.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    /* sessionStorage may be unavailable; still attempt the reload */
  }
  window.location.reload();
};
window.addEventListener("error", (event) => {
  reloadOnceForStaleChunks(event?.error || event?.message);
});
window.addEventListener("unhandledrejection", (event) => {
  reloadOnceForStaleChunks(event?.reason);
});

const { speechToTextURL, ...firebaseConfig } = firebaseConfigEnv;

initializeApp(firebaseConfig);

const persistConfig = {
  key: "root",
  storage,
  whitelist: [
    "patientDetailsReducer",
    "userReducer",
    "archivePatientReducer",
    "MiscReducer",
    "BreadCrumbReducer",
  ], // define which storage to use
};

let redux_middleware;
if (
  window.__REDUX_DEVTOOLS_EXTENSION__ &&
  process.env.NODE_ENV === "development"
) {
  redux_middleware = compose(
    applyMiddleware(thunk),
    window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__()
  );
} else {
  redux_middleware = compose(applyMiddleware(thunk));
}

const persistedReducer = persistReducer(persistConfig, reducer);

export const store = createStore(persistedReducer, redux_middleware);

const persistor = persistStore(store);
const rootElement = document.getElementById("root");
const mockInitialEntries =
  process.env.REACT_APP_ROUNDING_NOTES_MOCK === "true"
    ? ["/roundingNotesLocal"]
    : undefined;

if (rootElement) {
  ReactDOM.render(
    <MemoryRouter history={history} initialEntries={mockInitialEntries}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={GlobalTheme}>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <App />
            </PersistGate>
          </Provider>
        </ThemeProvider>
      </StyledEngineProvider>
    </MemoryRouter>,
    rootElement
  );
}

reportWebVitals();
